import { useRef, useState, useEffect } from "react";
import { motion, useSpring } from "framer-motion";

export function Magnet({
    children,
    padding = 50,
    disabled = false,
    magnetStrength = 2
}) {
    const [isActive, setIsActive] = useState(false);
    const ref = useRef(null);

    // Initial position
    const springConfig = { type: "spring", stiffness: 400, damping: 30 };
    const x = useSpring(0, springConfig);
    const y = useSpring(0, springConfig);

    const handleMouseMove = (e) => {
        if (disabled || !ref.current) return;

        const { clientX, clientY } = e;
        const { height, width, left, top } = ref.current.getBoundingClientRect();

        const centerX = left + width / 2;
        const centerY = top + height / 2;

        const dist = Math.sqrt(Math.pow(centerX - clientX, 2) + Math.pow(centerY - clientY, 2));

        if (dist < (width / 2 + padding)) {
            setIsActive(true);
            const offsetX = (clientX - centerX) / magnetStrength;
            const offsetY = (clientY - centerY) / magnetStrength;
            x.set(offsetX);
            y.set(offsetY);
        } else {
            setIsActive(false);
            x.set(0);
            y.set(0);
        }
    };

    const handleMouseLeave = () => {
        setIsActive(false);
        x.set(0);
        y.set(0);
    };

    useEffect(() => {
        if (isActive) {
            window.addEventListener("mousemove", handleMouseMove);
        } else {
            window.removeEventListener("mousemove", handleMouseMove);
            // Reset position when not active
            x.set(0);
            y.set(0);
        }

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
        };
    }, [isActive, disabled]); // Add disabled dependency

    // Also listen to mouse enter on the element itself to start tracking
    // But since we track global mouse move when near, we might need a different approach 
    // or just listen to mouse move on the parent container.
    // simpler approach: listen to mouse move on the element + padding area? 
    // ReactBits usually tracks mouse relative to the element.

    // Let's use a simpler, more robust implementation for typical button usage:
    // Track mouse move on the element itself.

    const handleRefMouseMove = (e) => {
        if (disabled) return;
        const { clientX, clientY } = e;
        const { height, width, left, top } = e.currentTarget.getBoundingClientRect();
        const middleX = clientX - (left + width / 2);
        const middleY = clientY - (top + height / 2);
        x.set(middleX / magnetStrength);
        y.set(middleY / magnetStrength);
    };

    const reset = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            onMouseMove={handleRefMouseMove}
            onMouseLeave={reset}
            style={{ x, y }}
            transition={{ type: "spring", stiffness: 150, damping: 15, mass: 0.1 }}
        >
            {children}
        </motion.div>
    );
}

export default Magnet;
